/*
  Build a Bundle — quick view.
  A card's "Details" button opens a modal that loads the product's page
  content and lets you pick a variant and add it to the bundle in one go.
*/
(function () {
  'use strict';

  const DRAWER_ID = 'build-a-bundle-quickview';
  const SECTION_ID = 'build-a-bundle-quickview';

  // Fetched section HTML kept by product handle so we don't refetch.
  const htmlCache = new Map();

  // The step the originating Details click came from. Read again at Add
  // time so the user's variant lands in the right bundle bucket.
  let activeStep = null;

  /* Modal lifecycle */

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function setStatus(state) {
    const drawer = getDrawer();
    if (drawer) drawer.dataset.status = state;
  }

  function clearSlot() {
    const slot = document.querySelector('.js-bundle-quickview-slot');
    if (slot) slot.replaceChildren();
  }

  function openModal() {
    if (window.wfhDrawer) window.wfhDrawer.open(DRAWER_ID);
  }

  function closeModal() {
    const drawer = getDrawer();
    if (drawer && window.wfhDrawer) window.wfhDrawer.close(drawer);
  }

  /* Section fetch */

  function fetchSection(handle) {
    if (htmlCache.has(handle)) {
      return Promise.resolve(htmlCache.get(handle));
    }
    const url =
      '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID;
    return fetch(url)
      .then((response) => {
        if (!response.ok)
          throw new Error('quickview fetch failed: ' + response.status);
        return response.text();
      })
      .then((html) => {
        htmlCache.set(handle, html);
        return html;
      });
  }

  function injectSection(html) {
    const slot = document.querySelector('.js-bundle-quickview-slot');
    if (!slot) return;
    slot.innerHTML = html;
    syncSizeGuide();
    rescanOkendo();
  }

  // Copy size guide HTML into the page-level drawer body. The drawer's
  // outside the quickview so its position: fixed isn't trapped by transform.
  function syncSizeGuide() {
    const slot = document.querySelector('.js-bundle-quickview-size-guide-slot');
    if (!slot) return;
    const template = document.querySelector(
      '.js-bundle-quickview-size-guide-content'
    );
    slot.innerHTML = template ? template.innerHTML : '';
  }

  // Rating is pre-rendered; only init an unrendered widget (re-init throws async).
  function rescanOkendo() {
    window.setTimeout(() => {
      const api = window.okeWidgetApi || window.oke;
      if (!api || typeof api.initWidget !== 'function') return;
      const slot = document.querySelector('.js-bundle-quickview-slot');
      const widget = slot && slot.querySelector('[data-oke-star-rating]');
      if (!widget || widget.querySelector('[data-oke-container]')) return;
      Promise.resolve(api.initWidget()).catch((error) => {
        console.warn('[build-a-bundle] okendo rescan failed', error);
      });
    }, 100);
  }

  /* Figuring out the selected variant — same as the card, but inside the quickview. */

  function getContentRoot() {
    return document.querySelector('.js-bundle-quickview-content');
  }

  function getVariants(root) {
    const dataEl = root.querySelector('.js-bundle-quickview-data');
    if (!dataEl) return [];
    try {
      return JSON.parse(dataEl.textContent).variants;
    } catch (error) {
      console.error('[build-a-bundle] could not parse quickview data', error);
      return [];
    }
  }

  function getSelectedColor(root) {
    const swatch = root.querySelector(
      '.js-bundle-quickview-swatch.is-selected'
    );
    return swatch ? swatch.dataset.color : null;
  }

  function getSelectedSize(root) {
    const tile = root.querySelector('.js-bundle-quickview-size.is-selected');
    return tile ? tile.dataset.size : null;
  }

  function findSelectedVariant(root) {
    const variants = getVariants(root);
    const color = getSelectedColor(root);
    const size = getSelectedSize(root);
    return variants.find(
      (v) =>
        (color === null || v.color === color) &&
        (size === null || v.size === size)
    );
  }

  // Update price block + image to match the resolved variant.
  function renderVariant(root, variant) {
    if (!variant) return;

    const priceEl = root.querySelector('.js-bundle-quickview-price');
    if (priceEl) {
      const onSale = variant.compareAt > variant.price;
      priceEl.classList.toggle(
        'build-a-bundle__quickview-price--on-sale',
        onSale
      );
      const saleEl = priceEl.querySelector('.js-bundle-quickview-price-sale');
      const compareEl = priceEl.querySelector(
        '.js-bundle-quickview-price-compare'
      );
      if (saleEl) saleEl.innerHTML = variant.priceMoney;
      if (compareEl) compareEl.innerHTML = variant.compareAtMoney;
    }

    const imageEl = root.querySelector('.js-bundle-quickview-image');
    if (imageEl && variant.image) imageEl.src = variant.image;
  }

  // Disable size tiles with no in-stock variant for the chosen colour;
  // re-point selection if the current tile just went unavailable.
  function refreshSizeTiles(root) {
    const tiles = root.querySelectorAll('.js-bundle-quickview-size');
    if (tiles.length === 0) return;

    const variants = getVariants(root);
    const color = getSelectedColor(root);
    let selectedStillValid = false;
    let firstAvailable = null;

    tiles.forEach((tile) => {
      const size = tile.dataset.size;
      const match = variants.find(
        (v) => v.size === size && (color === null || v.color === color)
      );
      const available = Boolean(match && match.available);
      tile.disabled = !available;
      if (available && !firstAvailable) firstAvailable = tile;
      if (tile.classList.contains('is-selected') && available) {
        selectedStillValid = true;
      }
    });

    if (!selectedStillValid && firstAvailable) {
      tiles.forEach((t) => t.classList.remove('is-selected'));
      firstAvailable.classList.add('is-selected');
      const sizeValueEl = root.querySelector('.js-bundle-quickview-size-value');
      if (sizeValueEl) sizeValueEl.textContent = firstAvailable.dataset.size;
    }
  }

  function bindSelectors(root) {
    const swatches = root.querySelectorAll('.js-bundle-quickview-swatch');
    const tiles = root.querySelectorAll('.js-bundle-quickview-size');

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        if (swatch.disabled) return;
        swatches.forEach((s) => s.classList.remove('is-selected'));
        swatch.classList.add('is-selected');

        const colorValueEl = root.querySelector(
          '.js-bundle-quickview-color-value'
        );
        if (colorValueEl) colorValueEl.textContent = swatch.dataset.color;

        refreshSizeTiles(root);
        renderVariant(root, findSelectedVariant(root));
      });
    });

    tiles.forEach((tile) => {
      tile.addEventListener('click', () => {
        if (tile.disabled) return;
        tiles.forEach((t) => t.classList.remove('is-selected'));
        tile.classList.add('is-selected');

        const sizeValueEl = root.querySelector(
          '.js-bundle-quickview-size-value'
        );
        if (sizeValueEl) sizeValueEl.textContent = tile.dataset.size;

        renderVariant(root, findSelectedVariant(root));
      });
    });
  }

  function initInjectedContent() {
    const root = getContentRoot();
    if (!root) return;
    refreshSizeTiles(root);
    renderVariant(root, findSelectedVariant(root));
    bindSelectors(root);
  }

  /* Add to Bundle — pulls the selection from the slot, writes to state */

  function onAddClick() {
    const root = getContentRoot();
    if (!root || !activeStep) return;

    const variant = findSelectedVariant(root);
    if (!variant || !variant.available) return;

    const titleEl = root.querySelector('.build-a-bundle__quickview-title');
    const title = titleEl ? titleEl.textContent.trim() : '';

    // Capture before close — the drawer:close listener resets activeStep.
    const step = activeStep;

    window.BuildABundleState.addItem(step, {
      variantId: variant.id,
      productId: root.dataset.productId,
      price: variant.price,
      compareAtPrice: variant.compareAt,
      title: title,
      image: variant.image,
      size: variant.size,
      color: variant.color,
    });

    // Close first so unlockScroll restores the page before activateStep's
    // scrollTo runs (scroll lock would otherwise swallow the navigation).
    closeModal();
    advanceBundleStep(step);
  }

  /* Open flow */

  function openQuickview(handle, step) {
    activeStep = step;
    clearSlot();
    setStatus('loading');
    openModal();

    fetchSection(handle)
      .then((html) => {
        // Bail if the user already closed before the fetch resolved.
        if (!getDrawer().classList.contains('is-open')) return;
        injectSection(html);
        initInjectedContent();
        setStatus('ready');
      })
      .catch((error) => {
        console.error('[build-a-bundle] quickview error', error);
        setStatus('error');
      });
  }

  /* Wiring */

  // Delegated Details click — works for any future async-rendered cards.
  document.addEventListener('click', (e) => {
    const button = e.target.closest('.js-bundle-card-details');
    if (!button) return;
    e.preventDefault();
    const handle = button.dataset.productHandle;
    const grid = button.closest('.js-bundle-grid');
    const step = grid ? grid.dataset.step : null;
    if (!handle || !step) return;
    openQuickview(handle, step);
  });

  // Add to Bundle is in the drawer's static footer, so it survives slot
  // re-renders and doesn't need rebinding per open.
  document.addEventListener('click', (e) => {
    if (e.target.closest('.js-bundle-quickview-add')) {
      onAddClick();
    }
  });

  // Wipe slot + reset status after the drawer's close transition so the
  // next open starts from a clean state. Leave the size-guide slot
  // alone — closing the quickview to reveal size-guide must not wipe it.
  document.addEventListener('drawer:close', (e) => {
    if (e.target && e.target.id === DRAWER_ID) {
      activeStep = null;
      clearSlot();
      setStatus('idle');
    }
  });
})();
